'use client';

import Image from 'next/image';
import { BorrowRecord } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, User, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BorrowRecordCardProps {
  record: BorrowRecord;
  onReturn?: (record: BorrowRecord) => void;
  onRenew?: (record: BorrowRecord) => void;
}

const statusConfig = {
  borrowed: { label: '借阅中', color: 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30' },
  returned: { label: '已归还', color: 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30' },
  overdue: { label: '已逾期', color: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/30' },
};

export function BorrowRecordCard({ record, onReturn, onRenew }: BorrowRecordCardProps) {
  const status = statusConfig[record.status];
  const isOverdue = record.status === 'overdue';
  const isReturned = record.status === 'returned';

  return (
    <div className={cn(
      "flex items-center gap-4 p-4 rounded-lg border transition-all",
      isOverdue 
        ? "bg-[#EF4444]/5 border-[#EF4444]/30" 
        : "bg-[#112240] border-[#1d3557] hover:border-[#64FFDA]/30"
    )}>
      <div className="relative w-14 h-20 rounded overflow-hidden flex-shrink-0">
        <Image
          src={record.bookCover}
          alt={record.bookTitle}
          fill
          className="object-cover"
          crossOrigin="anonymous"
        />
      </div>
      
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-[#E6F1FF] truncate">{record.bookTitle}</h3>
          <Badge className={cn("text-xs", status.color)}>
            {status.label}
          </Badge>
        </div>
        
        <div className="flex items-center gap-1 text-sm text-[#8892B0]">
          <User className="w-3.5 h-3.5" />
          <span>{record.userName}</span>
        </div>
        
        <div className="flex items-center gap-4 text-xs text-[#8892B0]">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            借出: {record.borrowDate}
          </span>
          <span className={cn(
            "flex items-center gap-1",
            isOverdue && "text-[#EF4444] font-medium"
          )}>
            <Clock className="w-3 h-3" />
            应还: {record.dueDate}
          </span>
          {record.returnDate && (
            <span className="flex items-center gap-1 text-[#10B981]">
              <Calendar className="w-3 h-3" />
              归还: {record.returnDate}
            </span>
          )}
        </div>
        
        {isOverdue && (
          <div className="flex items-center gap-1 text-xs text-[#EF4444]">
            <AlertCircle className="w-3 h-3" />
            <span>逾期罚款: ¥{record.fine}</span>
          </div>
        )}
      </div>
      
      {!isReturned && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
            onClick={() => onRenew?.(record)}
            disabled={isOverdue}
          >
            续借
          </Button>
          <Button
            size="sm"
            className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90"
            onClick={() => onReturn?.(record)}
          >
            归还
          </Button>
        </div>
      )}
    </div>
  );
}
